import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Space } from 'antd';
import { showMessage } from '@nxsys/utils/message';
import AppButton from '@nxsys/components/AppButton';
import {
  CompanyCreationMethodEnum,
  CompanyCreationTypeEnum,
  PAYMENT_MODEL_ENUM,
} from '@nxsys/constants/AppEnums';

// Component imports
import CompanyCreationMethod, {
  CompanyCreationMethodFormRef,
  CompanyCreationMethodValues,
} from './components/CompanyCreationMethod';
import CreationType, {
  CreationTypeFormRef,
  CreationTypeValues,
} from './components/CreationType';
import CompanyDetail, {
  CompanyDetailFormRef,
  CompanyDetailValues,
} from './components/CompanyDetail';
import HMRCSetting, {
  HMRCSettingFormRef,
  HMRCSettingValues,
} from './components/HMRCSetting';
import Department, {
  DepartmentFormRef,
  DepartmentValues,
} from './components/Department';
import CompanyDefault, {
  CompanyDefaultFormRef,
  CompanyDefaultValues,
} from './components/CompanyDefault';
import PaymentModel, {
  PaymentModelFormRef,
  PaymentModelValues,
} from './components/PaymentModel';
import Pension, {
  PensionFormRef,
  PensionStatement,
  PensionValues,
} from './components/Pension';
import PensionContinue, {
  PensionContinueFormRef,
  PensionContinueValues,
} from './components/PensionContinue';
import EmailSetting, {
  EmailSettingFormRef,
  EmailSettingType,
  EmailSettingValues,
} from './components/EmailSetting';
import SendCreationLink, {
  SendCreationLinkFormRef,
  SendCreationLinkValues,
} from './components/SendCreationLink';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import {
  useCreateCompany,
  useCreateCompanyDraft,
  useSendCompanyDraftLink,
  useSendTestEmailV2,
} from '@nxsys/hooks/api/company';
import { CreateCompanyParams } from '@nxsys/types/api/company';
import BankList, {
  BankListFormRef,
  BankListValues,
} from './components/BankList';
import RevenueSettings, {
  RevenueSettingsFormRef,
  RevenueSettingsValues,
} from './components/RevenueSettings';
import { toNumber } from 'lodash';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@nxsys/store';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { getArrayFromComaSeparateString } from '@nxsys/utils';

// Types and Enums
enum CREATE_COMPANY_STEP {
  COMPANY_CREATION_METHOD = 'COMPANY_CREATION_METHOD',
  CREATION_TYPE = 'CREATION_TYPE',
  COMPANY_DETAIL = 'COMPANY_DETAIL',
  HMRC_SETTING = 'HMRC_SETTING',
  REVENUE_SETTINGS = 'REVENUE_SETTINGS',
  DEPARTMENT = 'DEPARTMENT',
  COMPANY_DEFAULT = 'COMPANY_DEFAULT',
  PAYMENT_MODEL = 'PAYMENT_MODEL',
  EMAIL_SETTING = 'EMAIL_SETTING',
  PENSION = 'PENSION',
  PENSION_CONTINUE = 'PENSION_CONTINUE',
  BANK_LIST = 'BANK_LIST',
}

type FormValues = CompanyCreationMethodValues &
  CreationTypeValues &
  CompanyDetailValues &
  HMRCSettingValues &
  DepartmentValues &
  CompanyDefaultValues &
  PaymentModelValues &
  EmailSettingValues &
  RevenueSettingsValues &
  PensionValues &
  PensionContinueValues &
  BankListValues &
  SendCreationLinkValues;

type CreateCompanyModalProps = {
  isOpen: boolean;
  toggle: () => void;
  refetchList: () => void;
};

type ModalAction = {
  name: string;
  variant?: 'outlined' | 'text' | 'link';
  type?: 'primary' | 'default' | 'dashed';
  onClick: () => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
};

const CreateCompanyModal: React.FC<CreateCompanyModalProps> = ({
  isOpen,
  toggle,
  refetchList,
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  // Country-only: this wizard is the umbrella-Ireland admin's own path to
  // creating their company (per NX-5228 phase 5 decision — no BE auto-stub),
  // so `isBureau &&` meant it always fell through to the UK/bureau branch
  // below instead of the Ireland one.
  const isIreland = payrollCountry === IRELAND;

  const { run: createCompany, loading: createCompanyLoading } =
    useCreateCompany();
  const { run: createCompanyDraft, loading: createCompanyDraftLoading } =
    useCreateCompanyDraft();
  const { run: sendCompanyDraftLink, loading: sendLinkLoading } =
    useSendCompanyDraftLink();
  const { run: sendTestEmailV2, loading: sendTestEmailV2Loading } =
    useSendTestEmailV2();

  // Form refs
  const companyCreationMethodFormRef =
    useRef<CompanyCreationMethodFormRef>(null);
  const creationTypeFormRef = useRef<CreationTypeFormRef>(null);
  const companyDetailFormRef = useRef<CompanyDetailFormRef>(null);
  const hmrcSettingFormRef = useRef<HMRCSettingFormRef>(null);
  const revenueSettingsFormRef = useRef<RevenueSettingsFormRef>(null);
  const departmentFormRef = useRef<DepartmentFormRef>(null);
  const companyDefaultFormRef = useRef<CompanyDefaultFormRef>(null);
  const paymentModelFormRef = useRef<PaymentModelFormRef>(null);
  const emailSettingFormRef = useRef<EmailSettingFormRef>(null);
  const pensionFormRef = useRef<PensionFormRef>(null);
  const pensionContinueFormRef = useRef<PensionContinueFormRef>(null);
  const sendCreationLinkFormRef = useRef<SendCreationLinkFormRef>(null);
  const bankListFormRef = useRef<BankListFormRef>(null);

  // State management
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [formValues, setFormValues] = useState<FormValues | null>(null);
  const [showSendCreationLink, setShowSendCreationLink] = useState(false);
  const [emailSettingType, setEmailSettingType] = useState<EmailSettingType>(
    EmailSettingType.DEFAULT,
  );

  const steps = useMemo(() => {
    const allSteps: CREATE_COMPANY_STEP[] = [
      CREATE_COMPANY_STEP.COMPANY_CREATION_METHOD,
      CREATE_COMPANY_STEP.CREATION_TYPE,
      CREATE_COMPANY_STEP.COMPANY_DETAIL,
      ...(!isIreland
        ? [CREATE_COMPANY_STEP.HMRC_SETTING]
        : [CREATE_COMPANY_STEP.REVENUE_SETTINGS]),
      CREATE_COMPANY_STEP.BANK_LIST,
      CREATE_COMPANY_STEP.DEPARTMENT,
      CREATE_COMPANY_STEP.COMPANY_DEFAULT,
      CREATE_COMPANY_STEP.PAYMENT_MODEL,
      ...(!!formValues?.payeEnabled || !!formValues?.umbrellaEnabled
        ? [
            CREATE_COMPANY_STEP.PENSION,
            ...(!isIreland ? [CREATE_COMPANY_STEP.PENSION_CONTINUE] : []),
          ]
        : []),
      CREATE_COMPANY_STEP.EMAIL_SETTING,
    ];
    return allSteps;
  }, [isIreland, formValues]);

  // Step to form ref mapping
  const stepFormRefMap = useMemo(
    () => ({
      [CREATE_COMPANY_STEP.COMPANY_CREATION_METHOD]:
        companyCreationMethodFormRef,
      [CREATE_COMPANY_STEP.CREATION_TYPE]: creationTypeFormRef,
      [CREATE_COMPANY_STEP.COMPANY_DETAIL]: companyDetailFormRef,
      [CREATE_COMPANY_STEP.HMRC_SETTING]: hmrcSettingFormRef,
      [CREATE_COMPANY_STEP.REVENUE_SETTINGS]: revenueSettingsFormRef,
      [CREATE_COMPANY_STEP.DEPARTMENT]: departmentFormRef,
      [CREATE_COMPANY_STEP.COMPANY_DEFAULT]: companyDefaultFormRef,
      [CREATE_COMPANY_STEP.PAYMENT_MODEL]: paymentModelFormRef,
      [CREATE_COMPANY_STEP.EMAIL_SETTING]: emailSettingFormRef,
      [CREATE_COMPANY_STEP.PENSION]: pensionFormRef,
      [CREATE_COMPANY_STEP.PENSION_CONTINUE]: pensionContinueFormRef,
      [CREATE_COMPANY_STEP.BANK_LIST]: bankListFormRef,
    }),
    [],
  );

  // Step rendering
  const renderStep = useCallback(
    (step: CREATE_COMPANY_STEP) => {
      const stepNumber = getStepNumberLabelByStep(step);
      switch (step) {
        case CREATE_COMPANY_STEP.COMPANY_CREATION_METHOD:
          return (
            <CompanyCreationMethod
              formRef={companyCreationMethodFormRef}
              initialValues={formValues as CompanyCreationMethodValues}
            />
          );
        case CREATE_COMPANY_STEP.CREATION_TYPE:
          return (
            <CreationType
              formRef={creationTypeFormRef}
              initialValues={formValues as CreationTypeValues}
              stepNumber={stepNumber}
            />
          );
        case CREATE_COMPANY_STEP.COMPANY_DETAIL:
          return (
            <CompanyDetail
              formRef={companyDetailFormRef}
              initialValues={formValues as CompanyDetailValues}
              stepNumber={stepNumber}
            />
          );
        case CREATE_COMPANY_STEP.HMRC_SETTING:
          return (
            <HMRCSetting
              formRef={hmrcSettingFormRef}
              initialValues={formValues as HMRCSettingValues}
              creationType={formValues?.creationType as CompanyCreationTypeEnum}
              stepNumber={stepNumber}
            />
          );
        case CREATE_COMPANY_STEP.REVENUE_SETTINGS:
          return (
            <RevenueSettings
              formRef={revenueSettingsFormRef}
              initialValues={formValues as RevenueSettingsValues}
              stepNumber={stepNumber}
            />
          );
        case CREATE_COMPANY_STEP.DEPARTMENT:
          return (
            <Department
              formRef={departmentFormRef}
              initialValues={formValues as DepartmentValues}
              stepNumber={stepNumber}
            />
          );
        case CREATE_COMPANY_STEP.COMPANY_DEFAULT:
          return (
            <CompanyDefault
              formRef={companyDefaultFormRef}
              initialValues={formValues as CompanyDefaultValues}
              isIreland={isIreland}
              stepNumber={stepNumber}
            />
          );
        case CREATE_COMPANY_STEP.PAYMENT_MODEL:
          return (
            <PaymentModel
              formRef={paymentModelFormRef}
              initialValues={formValues as PaymentModelValues}
              stepNumber={stepNumber}
            />
          );
        case CREATE_COMPANY_STEP.EMAIL_SETTING:
          return (
            <EmailSetting
              formRef={emailSettingFormRef}
              initialValues={formValues as EmailSettingValues}
              stepNumber={stepNumber}
              onEmailSettingTypeChange={setEmailSettingType}
            />
          );
        case CREATE_COMPANY_STEP.PENSION:
          return (
            <Pension
              formRef={pensionFormRef}
              initialValues={formValues as PensionValues}
              isIreland={isIreland}
              stepNumber={stepNumber}
              creationType={formValues?.creationType as CompanyCreationTypeEnum}
            />
          );
        case CREATE_COMPANY_STEP.PENSION_CONTINUE:
          return (
            <PensionContinue
              formRef={pensionContinueFormRef}
              initialValues={formValues as PensionContinueValues}
              stepNumber={stepNumber}
            />
          );
        case CREATE_COMPANY_STEP.BANK_LIST:
          return (
            <BankList
              formRef={bankListFormRef}
              initialValues={formValues as BankListValues}
              stepNumber={stepNumber}
            />
          );
        default:
          return null;
      }
    },
    [formValues, isIreland],
  );

  // Step validation helper
  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    const currentStep = steps[activeStepIndex];
    const formRef = stepFormRefMap[currentStep]?.current;

    if (!formRef) {
      return true;
    }

    return (await formRef.isValidForm()) ?? false;
  }, [activeStepIndex, stepFormRefMap, steps]);

  // Get form values from current step helper
  const getCurrentStepValues = useCallback(async (): Promise<FormValues> => {
    const currentStep = steps[activeStepIndex];
    const formRef = stepFormRefMap[currentStep]?.current;

    if (!formRef) {
      return {} as FormValues;
    }

    return (formRef.getValues() as FormValues) ?? ({} as FormValues);
  }, [activeStepIndex, stepFormRefMap, steps]);

  // Navigation helpers
  const goToStep = useCallback(
    async (targetStepIndex: number) => {
      const currentValues = await getCurrentStepValues();
      setFormValues((prev) => ({
        ...prev,
        ...currentValues,
      }));
      setActiveStepIndex(targetStepIndex);
    },
    [getCurrentStepValues],
  );

  const handleConfirmCreationMethod = useCallback(() => {
    const value = companyCreationMethodFormRef.current?.getValues();
    const shouldShowSendLink =
      value?.creationMethod ===
      CompanyCreationMethodEnum.SEND_COMPANY_CREATION_LINK;

    if (shouldShowSendLink) {
      setShowSendCreationLink(true);
    } else {
      goToStep(1);
    }
  }, [goToStep]);

  const handleNextStep = useCallback(async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) {
      return;
    }

    if (
      steps.includes(CREATE_COMPANY_STEP.PENSION_CONTINUE) &&
      activeStepIndex === steps.indexOf(CREATE_COMPANY_STEP.PENSION)
    ) {
      const currentValues = await getCurrentStepValues();
      if (currentValues?.pensionStatement === PensionStatement.EXEMPT) {
        goToStep(activeStepIndex + 2);
        return;
      }
    }

    goToStep(activeStepIndex + 1);
  }, [activeStepIndex, validateCurrentStep, goToStep]);

  const handleGoBack = useCallback(async () => {
    if (showSendCreationLink) {
      setShowSendCreationLink(false);
      return;
    }

    if (
      steps.includes(CREATE_COMPANY_STEP.PENSION_CONTINUE) &&
      activeStepIndex ===
        steps.indexOf(CREATE_COMPANY_STEP.PENSION_CONTINUE) + 1
    ) {
      if (formValues?.pensionStatement === PensionStatement.EXEMPT) {
        goToStep(activeStepIndex - 2);
        return;
      }
    }

    goToStep(activeStepIndex - 1);
  }, [activeStepIndex, goToStep, showSendCreationLink, formValues]);

  // Action handlers
  const handleTestEmail = useCallback(async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) {
      return;
    }
    const currentValues = await getCurrentStepValues();
    const res = await sendTestEmailV2({
      smtpHost: currentValues?.smtpHost || '',
      smtpPort: currentValues?.smtpPort || '',
      smtpSecure: currentValues?.smtpSecure || false,
      smtpUser: currentValues?.smtpUser || '',
      smtpPassword: currentValues?.smtpPassword || '',
      emailSender: currentValues?.senderEmailId || '',
    });
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['company.create.testEmailSuccess'],
      });
    } else {
      showMessage({
        type: 'error',
        content: messages['company.create.testEmailFailed'],
      });
    }
  }, [validateCurrentStep, getCurrentStepValues, messages]);

  const handleComplete = useCallback(async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) {
      return;
    }

    const currentValues = await getCurrentStepValues();
    const finalValues = {
      ...formValues,
      ...currentValues,
    };
    const paymentModel = [];
    if (finalValues?.payeEnabled) paymentModel.push(PAYMENT_MODEL_ENUM.PAYEE);
    if (finalValues?.umbrellaEnabled)
      paymentModel.push(PAYMENT_MODEL_ENUM.UMBRELLA);
    if (finalValues?.payeHybridEnabled)
      paymentModel.push(PAYMENT_MODEL_ENUM.PAYE_HYBRID);
    if (finalValues?.umbrellaHybridEnabled)
      paymentModel.push(PAYMENT_MODEL_ENUM.HYBRID);
    if (finalValues?.cisEnabled) paymentModel.push(PAYMENT_MODEL_ENUM.CIS);
    if (finalValues?.payeFTHEnabled)
      paymentModel.push(PAYMENT_MODEL_ENUM.PAYE_FTH);

    let invoicingLogoUrl = '';
    if (finalValues?.companyLogo instanceof File) {
      try {
        const res = await dispatch(
          commonActions.uploadFileToS3({ file: finalValues.companyLogo }),
        ).unwrap();
        invoicingLogoUrl = res.url;
      } catch (error) {
        console.error(error);
        return;
      }
    } else if (typeof finalValues?.companyLogo === 'string') {
      invoicingLogoUrl = finalValues.companyLogo;
    }

    let payload: CreateCompanyParams = {} as CreateCompanyParams;

    if (!isIreland) {
      payload = {
        companyCreationType: finalValues?.creationType || null,
        companyDetail: {
          companyName: finalValues?.companyName || null,
          tradingName: finalValues?.tradingName || null,
          address: [
            finalValues?.addressLine1 || '',
            finalValues?.addressLine2 || '',
            finalValues?.addressLine3 || '',
          ],
          city: finalValues?.city || null,
          county:
            typeof finalValues?.county === 'object'
              ? (finalValues?.county as any)?.value || null
              : finalValues?.county || null,
          postCode: finalValues?.postCode || null,
          country: finalValues?.country?.value || null,
        },
        hmrcSettings: {
          employerSettings: {
            taxDistrict: finalValues?.taxDistrict || null,
            employeeReference: finalValues?.employerPayeReference || null,
            accountsOfficeReference:
              finalValues?.accountsOfficeReference || null,
            claimEmpAllowance: finalValues?.claimEmploymentAllowance || false,
            allowanceClaimableEmpAllowance:
              finalValues?.allowanceClaimableEmpAllowance
                ? toNumber(finalValues?.allowanceClaimableEmpAllowance)
                : null,
            calculateAppLevy: finalValues?.claimApprenticeshipLevy || false,
            eligibleForSmallEmpRelief:
              finalValues?.eligibleForSmallEmpRelief || false,
            allowanceClaimableAppLevy: finalValues?.allowanceClaimableAppLevy
              ? toNumber(finalValues?.allowanceClaimableAppLevy)
              : null,
            deductionRate: finalValues?.deductionRate
              ? toNumber(finalValues?.deductionRate)
              : null,
            openingBalances: {
              recoverableAmountsYTD:
                finalValues?.recoverableAmountsYTD || false,
              smpRecoveredYTD: finalValues?.smpRecoveredYtd
                ? toNumber(finalValues?.smpRecoveredYtd)
                : null,
              sppRecoveredYTD: finalValues?.sppRecoveredYtd
                ? toNumber(finalValues?.sppRecoveredYtd)
                : null,
              sapRecoveredYTD: finalValues?.sapRecoveredYtd
                ? toNumber(finalValues?.sapRecoveredYtd)
                : null,
              shppRecoveredYTD: finalValues?.shppRecoveredYtd
                ? toNumber(finalValues?.shppRecoveredYtd)
                : null,
              spbpRecoveredYTD: finalValues?.spbpRecoveredYtd
                ? toNumber(finalValues?.spbpRecoveredYtd)
                : null,
              sncpRecoveredYTD: finalValues?.sncpRecoveredYtd
                ? toNumber(finalValues?.sncpRecoveredYtd)
                : null,
              nicCompensationOnSmpYTD: finalValues?.nicCompensationOnSmpYtd
                ? toNumber(finalValues?.nicCompensationOnSmpYtd)
                : null,
              nicCompensationOnSppYTD: finalValues?.nicCompensationOnSppYtd
                ? toNumber(finalValues?.nicCompensationOnSppYtd)
                : null,
              nicCompensationOnSapYTD: finalValues?.nicCompensationOnSapYtd
                ? toNumber(finalValues?.nicCompensationOnSapYtd)
                : null,
              nicCompensationOnShppYTD: finalValues?.nicCompensationOnShppYtd
                ? toNumber(finalValues?.nicCompensationOnShppYtd)
                : null,
              nicCompensationOnSpbpYTD: finalValues?.nicCompensationOnSpbpYtd
                ? toNumber(finalValues?.nicCompensationOnSpbpYtd)
                : null,
              nicCompensationOnSncpYTD: finalValues?.nicCompensationOnSncpYtd
                ? toNumber(finalValues?.nicCompensationOnSncpYtd)
                : null,
              cisDeductionsSufferedYTD: finalValues?.cisDeductionSufferedYtd
                ? toNumber(finalValues?.cisDeductionSufferedYtd)
                : null,
              appLevyClaimedYTD: finalValues?.appLevyClaimedYtd
                ? toNumber(finalValues?.appLevyClaimedYtd)
                : null,
              allowanceClaimedSoFar: finalValues?.empAllowanceClaimedYtd
                ? toNumber(finalValues?.empAllowanceClaimedYtd)
                : null,
            },
          },
          rtiSettings: {
            enableRTI: finalValues?.enableRTI || false,
            userId: finalValues?.userId || null,
            password: finalValues?.password || null,
            submitAsAgent: finalValues?.submitAsAgent || false,
            agentDetails: {
              forename: finalValues?.agentForename || null,
              surname: finalValues?.agentSurname || null,
              address: [
                finalValues?.agentAddressLine1 || '',
                finalValues?.agentAddressLine2 || '',
              ],
              city: finalValues?.agentCityTown || null,
              county: null,
              country: finalValues?.agentCountry?.value || null,
              postCode: finalValues?.agentPostCode || null,
            },
          },
        },
        departments:
          finalValues?.departments?.map((d) => ({
            departmentName: d?.department || null,
            isSameAsCompany: d?.isSameAsCompany || false,
          })) || [],
        defaults: {
          paymentTerm: finalValues?.paymentTerm?.value || null,
          taxCode: finalValues?.taxCode || null,
          NICategory: finalValues?.NICategory?.value || null,
          paymentFrequency: finalValues?.paymentFrequency?.value || null,
          paymentMethod: finalValues?.paymentMethod?.value || null,
          candidateStatus: finalValues?.candidateStatus?.value || null,
          passProtectDoc: finalValues?.passProtectDoc ?? null,
          agencyRefMethod: finalValues?.agencyRefMethod?.value || 'NI_NUMBER',
          marginMethod: finalValues?.marginMethod?.value || null,
          margin: finalValues?.margin || null,
          marginCapped: finalValues?.marginCapped || null,
          minMargin: finalValues?.minMargin || null,
          timesheetImportValidation:
            finalValues?.timesheetImportValidation?.value ||
            'AGENCY_REFERENCE_FORENAME_SURNAME',
          isRestrictedHours: finalValues?.isRestrictedHours ?? null,
          isRequiredAttachmentExpenses:
            finalValues?.isRequiredAttachmentExpenses ?? null,
          invoicePrefix: finalValues?.invoicePrefix || null,
          invoiceNextNumber: finalValues?.invoiceNextNumber || null,
          creditNotePrefix: finalValues?.creditNotePrefix || null,
          creditNoteNextNumber: finalValues?.creditNoteNextNumber || null,
          subOnboarding: finalValues?.subOnboarding ?? null,
          onboarding:
            finalValues?.onboarding?.map((o) => o?.value || null) || [],
          UKVerifiedIdMethod: finalValues?.UKVerifiedIdMethod?.value || null,
          nonUKVerifiedIdMethod:
            finalValues?.nonUKVerifiedIdMethod?.value || null,
          emailOnboardingNotification:
            finalValues?.emailOnboardingNotification || null,
          enableAWRTracking: finalValues?.enableAWRTracking ?? null,
          notificationWeeks: finalValues?.notificationWeeks
            ? (getArrayFromComaSeparateString(
                finalValues?.notificationWeeks as string,
              ) as number[])
            : null,
          enableWTR: finalValues?.enableWTR ?? null,
          isNotSameAsCompany:
            typeof finalValues?.invoicingDetailsSameAsCompanyDetails ===
            'boolean'
              ? !finalValues?.invoicingDetailsSameAsCompanyDetails
              : null,
          enableBenefits: finalValues?.enableBenefits ?? null,
          invoicingCompanyName: finalValues?.invoicingCompanyName || '',
          invoicingCompanyNo: finalValues?.invoicingCompanyNo || '',
          invoicingTradingName: finalValues?.invoicingTradingName || '',
          invoicingAddress1: finalValues?.invoicingAddress1 || '',
          invoicingAddress2: finalValues?.invoicingAddress2 || '',
          invoicingAddress3: finalValues?.invoicingAddress3 || '',
          invoicingAddress4: finalValues?.invoicingAddress4 || '',
          invoicingPostCode: finalValues?.invoicingPostCode || '',
          invoicingCity: finalValues?.invoicingCity || '',
          invoicingCountry: finalValues?.invoicingCountry?.value || '',
          invoicingCounty: finalValues?.invoicingCounty || '',
          invoicingVATRegNo: finalValues?.invoicingVATRegNo || '',
          invoicingLogoUrl,
        },
        paymentModelConfig: {
          paymentModel,
          paye: finalValues?.payeEnabled
            ? {
                enableHolidayPay: finalValues?.payeHolidayPay ?? null,
                payeHolidayPayRate: finalValues?.payeHolidayPayRate
                  ? toNumber(finalValues?.payeHolidayPayRate)
                  : null,
                holidayPayMethod:
                  finalValues?.payeHolidayPayMethod?.value || null,
                holidayPayCalculationRule:
                  finalValues?.payeHolidayPayCalculationRule?.value || null,
                includeInTimesheetRate:
                  finalValues?.payeIncludedInTimesheetRate ?? null,
                payePayslipTemplate:
                  finalValues?.payePayslipTemplate?.value || null,
              }
            : undefined,
          umbrella: finalValues?.umbrellaEnabled
            ? {
                splitSalaryAndBonus:
                  finalValues?.umbrellaSplitSalaryBonus ?? null,
                umbrellaPayslipTemplate:
                  finalValues?.umbrellaPayslipTemplate?.value || null,
                enableHolidayPayUmbrella:
                  finalValues?.umbrellaHolidayPay ?? null,
                umbrellaHolidayPayRate: finalValues?.umbrellaHolidayPayRate
                  ? toNumber(finalValues?.umbrellaHolidayPayRate)
                  : null,
                holidayPayMethod:
                  finalValues?.umbrellaHolidayPayMethod?.value || null,
                holidayPayCalculationRule:
                  finalValues?.umbrellaHolidayPayCalculationRule?.value || null,
                reclaimAppLevyCosts:
                  finalValues?.umbrellaReclaimAppLevyCosts ?? null,
                accrueEmploymentCosts:
                  finalValues?.umbrellaAccrueEmploymentCosts ?? null,
              }
            : undefined,
          payeHybrid: finalValues?.payeHybridEnabled
            ? {
                hybridPayePayslipTemplate:
                  finalValues?.hybridPayePayslipTemplate?.value || null,
              }
            : undefined,
          umbrellaHybrid: finalValues?.umbrellaHybridEnabled
            ? {
                reclaimAppLevyCosts:
                  finalValues?.umbrellaHybridReclaimAppLevyCosts ?? null,
                hybridPayslipTemplate:
                  finalValues?.hybridPayslipTemplate?.value || null,
              }
            : undefined,
          cis: finalValues?.cisEnabled
            ? {
                cisPayslipTemplate:
                  finalValues?.cisPayslipTemplate?.value || null,
              }
            : undefined,
          payeFTH: {
            taxFreePaymentDesc: finalValues?.taxFreePaymentDesc || null,
            payeFthMarginAdjustment:
              finalValues?.payeFthMarginAdjustment ?? null,
            payeFTHPayslipTemplate:
              finalValues?.payeFTHPayslipTemplate?.value || null,
          },
        },
        pensionConfig: {
          pensionOption: finalValues?.pensionStatement || null,
          stagingDate:
            finalValues?.pensionStatement === PensionStatement.EMPLOYING_BEFORE
              ? finalValues?.stagingDate?.format('YYYY-MM-DD') || null
              : null,
          reEnrolmentDate:
            finalValues?.pensionStatement === PensionStatement.EMPLOYING_BEFORE
              ? finalValues?.nextReEnrolmentDate?.format('YYYY-MM-DD') || null
              : null,
          providerConfig:
            finalValues?.pensionStatement !== PensionStatement.EXEMPT &&
            finalValues?.pensionProviderId
              ? {
                  pensionProviderId:
                    finalValues.pensionProviderId?.value || null,
                  aePostponementDays:
                    finalValues.aePostponementDays?.value || null,
                  erPensionPercentage:
                    finalValues.erPensionPercentage != null
                      ? Number(finalValues.erPensionPercentage)
                      : null,
                  eePensionPercentage:
                    finalValues.eePensionPercentage != null
                      ? Number(finalValues.eePensionPercentage)
                      : null,
                  pensionSelfCertification:
                    finalValues.pensionSelfCertification || null,
                  pensionCalculationType:
                    finalValues.pensionCalculationType?.value ||
                    'NET_PAY_ARRANGEMENT',
                  pensionMethod:
                    finalValues.pensionMethod?.value || 'QUALIFYING_EARNINGS',
                  isSalarySacrifice: finalValues.isSalarySacrifice ?? null,
                  companyId: finalValues.companyId || null,
                  pensionSchemeId: finalValues.pensionSchemeId || null,
                  pensionSchemeNumber: finalValues.pensionSchemeNumber || null,
                  employerCode: finalValues.employerCode || null,
                  payrollCode: finalValues.payrollCode || null,
                  schemeCodeWeekly: finalValues.schemeCodeWeekly || null,
                  schemeCode2Weekly: finalValues.schemeCode2Weekly || null,
                  schemeCode4Weekly: finalValues.schemeCode4Weekly || null,
                  schemeCodeMonthly: finalValues.schemeCodeMonthly || null,
                  jarvisGroupOrganizationApiKey:
                    finalValues.jarvisGroupOrganizationApiKey || null,
                  jarvisOrganizationID:
                    finalValues.jarvisOrganizationID || null,
                  jarvisPensionScheme:
                    finalValues.jarvisPensionScheme || null,
                }
              : null,
        },
        emailConfig: {
          emailType: finalValues?.emailSettingType || null,
          emailSender: finalValues?.senderEmailId || null,
          smtpHost: finalValues?.smtpHost || null,
          smtpPort: finalValues?.smtpPort
            ? toNumber(finalValues?.smtpPort)
            : null,
          smtpSecure: finalValues?.smtpSecure || false,
          smtpUser: finalValues?.smtpUser || null,
          smtpPassword: finalValues?.smtpPassword || null,
        },
        banksList:
          finalValues?.banksList?.map((b) => ({
            bankName: b?.bankName?.value || null,
            accountName: b?.accountName || null,
            accountNumber: b?.accountNumber || null,
            sortCode: b?.sortCode || null,
            isPrimary: b?.isPrimary || false,
          })) || [],
        draftId: finalValues?.companyDraftId || null,
      };
    } else {
      // Upload P12 file to S3 if a new file was selected
      let p12FileLinkUrl: string | null = null;
      if (finalValues?.p12FileLink instanceof File) {
        try {
          const res = await dispatch(
            commonActions.uploadFileToS3({ file: finalValues.p12FileLink }),
          ).unwrap();
          p12FileLinkUrl = res.url;
        } catch (error) {
          console.error(error);
          return;
        }
      } else {
        p12FileLinkUrl = (finalValues?.p12FileLink as string) || null;
      }

      payload = {
        companyCreationType: finalValues?.creationType || null,
        companyDetail: {
          companyName: finalValues?.companyName || null,
          tradingName: finalValues?.tradingName || null,
          address: [
            finalValues?.addressLine1 || '',
            finalValues?.addressLine2 || '',
            finalValues?.addressLine3 || '',
          ],
          city: finalValues?.city || null,
          county:
            typeof finalValues?.county === 'object'
              ? (finalValues?.county as any)?.value || null
              : finalValues?.county || null,
          postCode: finalValues?.postCode || null,
          country: finalValues?.country?.value || null,
        },
        departments:
          finalValues?.departments?.map((d) => ({
            departmentName: d?.department || null,
            isSameAsCompany: d?.isSameAsCompany || false,
            companyName: d?.companyName || null,
            address: [
              d?.address1 || '',
              d?.address2 || '',
              d?.address3 || '',
              d?.address4 || '',
            ],
            city: d?.city || null,
            county: d?.county || null,
            postCode: d?.postCode || null,
            country: d?.country?.value || null,
            email: d?.email || null,
          })) || [],
        defaults: {
          paymentTerm: finalValues?.paymentTerm?.value || null,
          paymentFrequency: finalValues?.paymentFrequency?.value || null,
          paymentMethod: finalValues?.paymentMethod?.value || null,
          candidateStatus: finalValues?.candidateStatus?.value || null,
          passProtectDoc: finalValues?.passProtectDoc ?? null,
          agencyRefMethod: finalValues?.agencyRefMethod?.value || 'NI_NUMBER',
          isRequiredAttachmentExpenses:
            finalValues?.isRequiredAttachmentExpenses ?? null,
          taxBasis: finalValues?.taxBasis?.value || null,
          prsiEeClasses: finalValues?.prsiEeClasses?.value || null,
          marginMethod: finalValues?.marginMethod?.value || null,
          margin: finalValues?.margin || null,
          marginCapped: finalValues?.marginCapped || null,
          minMargin: finalValues?.minMargin || null,
          timesheetImportValidation:
            finalValues?.timesheetImportValidation?.value ||
            'AGENCY_REFERENCE_FORENAME_SURNAME',
          isRestrictedHours: finalValues?.isRestrictedHours ?? false,
          invoicePrefix: finalValues?.invoicePrefix || null,
          invoiceNextNumber: finalValues?.invoiceNextNumber || null,
          creditNotePrefix: finalValues?.creditNotePrefix || null,
          creditNoteNextNumber: finalValues?.creditNoteNextNumber || null,
          subOnboarding: finalValues?.subOnboarding ?? null,
          onboarding:
            finalValues?.onboarding?.map((o) => o?.value || null) || [],
          UKVerifiedIdMethod: finalValues?.UKVerifiedIdMethod?.value || null,
          emailOnboardingNotification:
            finalValues?.emailOnboardingNotification || null,
          nonUKVerifiedIdMethod:
            finalValues?.nonUKVerifiedIdMethod?.value || null,
          enableAWRTracking: finalValues?.enableAWRTracking ?? null,
          notificationWeeks: finalValues?.notificationWeeks
            ? (getArrayFromComaSeparateString(
                finalValues?.notificationWeeks as string,
              ) as number[])
            : null,
          enableWTR: finalValues?.enableWTR ?? null,
          isNotSameAsCompany:
            typeof finalValues?.invoicingDetailsSameAsCompanyDetails ===
            'boolean'
              ? !finalValues?.invoicingDetailsSameAsCompanyDetails
              : null,
          enableBenefits: finalValues?.enableBenefits ?? null,
          invoicingCompanyName: finalValues?.invoicingCompanyName || '',
          invoicingCompanyNo: finalValues?.invoicingCompanyNo || '',
          invoicingTradingName: finalValues?.invoicingTradingName || '',
          invoicingAddress1: finalValues?.invoicingAddress1 || '',
          invoicingAddress2: finalValues?.invoicingAddress2 || '',
          invoicingAddress3: finalValues?.invoicingAddress3 || '',
          invoicingAddress4: finalValues?.invoicingAddress4 || '',
          invoicingPostCode: finalValues?.invoicingPostCode || '',
          invoicingCity: finalValues?.invoicingCity || '',
          invoicingCountry: finalValues?.invoicingCountry?.value || '',
          invoicingCounty: finalValues?.invoicingCounty || '',
          invoicingVATRegNo: finalValues?.invoicingVATRegNo || '',
          invoicingLogoUrl,
        },
        paymentModelConfig: {
          paymentModel,
          paye: {
            enableHolidayPay: finalValues?.payeHolidayPay ?? null,
            payeHolidayPayRate: finalValues?.payeHolidayPayRate
              ? toNumber(finalValues?.payeHolidayPayRate)
              : null,
            holidayPayMethod: finalValues?.payeHolidayPayMethod?.value || null,
            holidayPayCalculationRule:
              finalValues?.payeHolidayPayCalculationRule?.value || null,
            includeInTimesheetRate:
              finalValues?.payeIncludedInTimesheetRate ?? null,
            payePayslipTemplate:
              finalValues?.payePayslipTemplate?.value || null,
          },
          umbrella: finalValues?.umbrellaEnabled
            ? {
                splitSalaryAndBonus:
                  finalValues?.umbrellaSplitSalaryBonus ?? null,
                umbrellaPayslipTemplate:
                  finalValues?.umbrellaPayslipTemplate?.value || null,
                enableHolidayPayUmbrella: finalValues?.umbrellaHolidayPay ?? null,
                umbrellaHolidayPayRate: finalValues?.umbrellaHolidayPayRate
                  ? toNumber(finalValues?.umbrellaHolidayPayRate)
                  : null,
                holidayPayMethod:
                  finalValues?.umbrellaHolidayPayMethod?.value || null,
                holidayPayCalculationRule:
                  finalValues?.umbrellaHolidayPayCalculationRule?.value || null,
                reclaimAppLevyCosts:
                  finalValues?.umbrellaReclaimAppLevyCosts ?? null,
                accrueEmploymentCosts:
                  finalValues?.umbrellaAccrueEmploymentCosts ?? null,
              }
            : undefined,
          payeHybrid: {
            hybridPayePayslipTemplate: null,
          },
          umbrellaHybrid: {
            reclaimAppLevyCosts: null,
            hybridPayslipTemplate: null,
          },
          cis: {
            cisPayslipTemplate: finalValues?.cisPayslipTemplate?.value || null,
          },
          payeFTH: {
            taxFreePaymentDesc: null,
            payeFthMarginAdjustment: null,
            payeFTHPayslipTemplate: null,
          },
        },
        pensionConfig: {
          pensionOption: null,
          stagingDate: null,
          reEnrolmentDate: null,
          providerConfig: null,
          revenueCredentials: {
            employerRegistrationNumber:
              finalValues?.employerRegistrationNumber || null,
            p12FileLink: p12FileLinkUrl,
            passwordP12: finalValues?.passwordP12 || null,
          },
          irelandPrivatePensionSchemes:
            finalValues?.irelandPrivatePensionSchemes?.map((s) => ({
              type: s?.type?.value || '',
              name: s?.name || '',
              reference: s?.reference || '',
              eePensionValue: s?.eePensionValue
                ? toNumber(s.eePensionValue)
                : 0,
              eePensionType: s?.eePensionType?.value || 'PERCENTAGE',
              erPensionValue: s?.erPensionValue
                ? toNumber(s.erPensionValue)
                : 0,
              erPensionType: s?.erPensionType?.value || 'PERCENTAGE',
              avcPensionValue: s?.avcPensionValue
                ? toNumber(s.avcPensionValue)
                : 0,
              avcPensionType: s?.avcPensionType?.value || 'PERCENTAGE',
              isDefault: s?.isDefault || false,
            })) || [],
          irelandPensionContactPayment:
            finalValues?.contactPaymentSameAsEmployer
              ? (() => {
                  const primaryBank =
                    finalValues?.banksList?.find((b) => b?.isPrimary) ||
                    finalValues?.banksList?.[0];
                  return {
                    address: [
                      finalValues?.addressLine1,
                      finalValues?.addressLine2,
                      finalValues?.addressLine3,
                    ].filter(Boolean),
                    eirCode: finalValues?.postCode || undefined,
                    phone: undefined,
                    bankName:
                      (typeof primaryBank?.bankName === 'object'
                        ? (primaryBank?.bankName as any)?.value
                        : primaryBank?.bankName) || undefined,
                    bankBranch: undefined,
                    bankSortCode: primaryBank?.sortCode || undefined,
                    bankAccountNumber: primaryBank?.accountNumber || undefined,
                    bankAccountName: primaryBank?.accountName || undefined,
                    bankReference: primaryBank?.bankReference || undefined,
                    IBAN: primaryBank?.IBANReference || undefined,
                    BIC: primaryBank?.BIC || undefined,
                  };
                })()
              : {
                  address: [
                    finalValues?.address1,
                    finalValues?.address2,
                    finalValues?.address3,
                    finalValues?.address4,
                  ].filter(Boolean),
                  eirCode: finalValues?.eircode || undefined,
                  phone: finalValues?.phone || undefined,
                  bankName: finalValues?.bankName || undefined,
                  bankBranch: finalValues?.bankBranch || undefined,
                  bankSortCode: finalValues?.bankSortCode || undefined,
                  bankAccountNumber: finalValues?.accountNumber || undefined,
                  bankAccountName: finalValues?.bankAccountName || undefined,
                  bankReference: finalValues?.bankReference || undefined,
                  IBAN: finalValues?.iban || undefined,
                  BIC: finalValues?.bic || undefined,
                },
        },
        emailConfig: {
          emailType: finalValues?.emailSettingType || null,
          emailSender: finalValues?.senderEmailId || null,
          smtpHost: finalValues?.smtpHost || null,
          smtpPort: finalValues?.smtpPort
            ? toNumber(finalValues?.smtpPort)
            : null,
          smtpSecure: finalValues?.smtpSecure || false,
          smtpUser: finalValues?.smtpUser || null,
          smtpPassword: finalValues?.smtpPassword || null,
        },
        banksList:
          finalValues?.banksList?.map((b) => ({
            bankName: b?.bankName?.value || null,
            accountName: b?.accountName || null,
            accountNumber: b?.accountNumber || null,
            sortCode: b?.sortCode || null,
            isPrimary: b?.isPrimary || false,
            IBANReference: b?.IBANReference || null,
            orgId: b?.orgId || null,
            bankReference: b?.bankReference || null,
            BIC: b?.BIC || null,
          })) || [],
      };
    }

    const res = await createCompany(payload);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['company.create.success'],
      });
      // The company was created; HMRC refused the RTI credentials on it.
      if (res.warning) {
        showMessage({ type: 'warning', content: res.warning, duration: 10 });
      }
      resetFieldsAndStates();
      toggle();
      refetchList();
    }
  }, [
    formValues,
    validateCurrentStep,
    getCurrentStepValues,
    messages,
    createCompany,
    toggle,
    isBureau,
    isIreland,
  ]);

  const resetFieldsAndStates = () => {
    companyCreationMethodFormRef.current?.resetFields();
    creationTypeFormRef.current?.resetFields();
    companyDetailFormRef.current?.resetFields();
    hmrcSettingFormRef.current?.resetFields();
    departmentFormRef.current?.resetFields();
    companyDefaultFormRef.current?.resetFields();
    paymentModelFormRef.current?.resetFields();
    emailSettingFormRef.current?.resetFields();
    pensionFormRef.current?.resetFields();
    pensionContinueFormRef.current?.resetFields();
    bankListFormRef.current?.resetFields();
    sendCreationLinkFormRef.current?.resetFields();
    setFormValues(null);
    setActiveStepIndex(0);
    setShowSendCreationLink(false);
    setEmailSettingType(EmailSettingType.DEFAULT);
  };

  const handleSendCreationLink = useCallback(async () => {
    const isValid = await sendCreationLinkFormRef.current?.isValidForm();
    if (!isValid) {
      return;
    }
    const currentValues = await sendCreationLinkFormRef.current?.getValues();
    const companyDraft = await createCompanyDraft({
      frontendData: {},
    });
    if (!companyDraft.id) return;
    await sendCompanyDraftLink(companyDraft.id, {
      email: currentValues!.email,
      recipientName: currentValues!.recipientName,
      isBureau: isBureau,
    });
    showMessage({
      type: 'success',
      content: messages['company.create.sendLinkSuccess'],
    });
  }, [createCompanyDraft, sendCompanyDraftLink, messages, toggle]);

  const handleCancel = useCallback(() => {
    toggle();
    resetFieldsAndStates();
  }, [resetFieldsAndStates, toggle]);

  // Modal actions configuration
  const modalActions = useMemo((): ModalAction[] => {
    // Send creation link actions
    if (showSendCreationLink) {
      return [
        {
          name: messages['common.goBack'],
          variant: 'outlined',
          onClick: handleGoBack,
        },
        {
          name: messages['common.send'],
          type: 'primary',
          onClick: handleSendCreationLink,
          loading: sendLinkLoading || createCompanyDraftLoading,
          disabled: sendLinkLoading || createCompanyDraftLoading,
        },
      ];
    }

    const isFirstStep = activeStepIndex === 0;
    const isLastStep = activeStepIndex === steps.length - 1;
    const isEmailSettingStep =
      steps[activeStepIndex] === CREATE_COMPANY_STEP.EMAIL_SETTING;

    // First step actions
    if (isFirstStep) {
      return [
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: handleCancel,
        },
        {
          name: messages['common.confirm'],
          type: 'primary',
          onClick: handleConfirmCreationMethod,
        },
      ];
    }

    // Regular step actions
    const actions: ModalAction[] = [
      {
        name: messages['common.cancel'],
        variant: 'outlined',
        onClick: handleCancel,
      },
    ];

    // Add test email button for email setting step (only for custom email)
    if (isEmailSettingStep && emailSettingType !== EmailSettingType.DEFAULT) {
      actions.push({
        name: messages['company.emailSettings.testEmail'],
        variant: 'outlined',
        onClick: handleTestEmail,
        loading: sendTestEmailV2Loading,
        disabled: sendTestEmailV2Loading,
      });
    }

    // Add navigation buttons
    actions.push(
      {
        name: messages['common.goBack'],
        variant: 'outlined',
        onClick: handleGoBack,
      },
      {
        name: isLastStep ? messages['common.create'] : messages['common.next'],
        type: 'primary',
        onClick: isLastStep ? handleComplete : handleNextStep,
        loading: createCompanyLoading,
        disabled: createCompanyLoading,
      },
    );

    return actions;
  }, [
    activeStepIndex,
    steps,
    toggle,
    handleConfirmCreationMethod,
    handleGoBack,
    handleNextStep,
    handleComplete,
    handleTestEmail,
    handleSendCreationLink,
    showSendCreationLink,
    messages,
    createCompanyLoading,
    sendLinkLoading,
    createCompanyDraftLoading,
    emailSettingType,
    sendTestEmailV2Loading,
  ]);

  const getStepNumberLabelByStep = useCallback(
    (step: CREATE_COMPANY_STEP) => {
      if (steps.includes(CREATE_COMPANY_STEP.PENSION_CONTINUE)) {
        if (step === CREATE_COMPANY_STEP.PENSION_CONTINUE) return 8;
        if (
          steps.indexOf(step) >
          steps.indexOf(CREATE_COMPANY_STEP.PENSION_CONTINUE)
        )
          return steps.indexOf(step) - 1;
      }
      return steps.indexOf(step);
    },
    [steps],
  );

  const totalSteps = useMemo(() => {
    if (steps.includes(CREATE_COMPANY_STEP.PENSION_CONTINUE)) {
      return steps.length - 2;
    }
    return steps.length - 1;
  }, [steps]);

  const currentStep = useMemo(() => {
    return steps[activeStepIndex];
  }, [activeStepIndex, steps]);

  return (
    <AppModalContainer
      title={null}
      isOpen={isOpen}
      toggle={toggle}
      width={1000}
      className="max-w-screen"
      minHeight="0px"
      footer={
        <div className="flex justify-between items-center">
          <div className="text-[14px]">
            {activeStepIndex > 0 && (
              <>
                <span className="text-primary-600">{`${messages['common.step']} ${getStepNumberLabelByStep(currentStep)}`}</span>
                /{totalSteps}
              </>
            )}
          </div>
          <div className="flex items-center gap-x-[8px]">
            <Space>
              {modalActions.map((action, index) => (
                <AppButton
                  key={index}
                  name={action.name}
                  variant={action.variant}
                  type={action.type}
                  onClick={action.onClick}
                  loading={action.loading}
                  disabled={action.disabled}
                />
              ))}
            </Space>
          </div>
        </div>
      }
    >
      {showSendCreationLink ? (
        <SendCreationLink
          formRef={sendCreationLinkFormRef}
          initialValues={formValues as SendCreationLinkValues}
        />
      ) : (
        renderStep(currentStep)
      )}
    </AppModalContainer>
  );
};

export default CreateCompanyModal;
